import filters from 'data/filters';
import { keepEssentialFilters } from 'flux/stores/FiltersStore';
import isEqual from 'lodash.isequal';
import { ParseReturn } from 'model/UrlParsing';
import SearchLoading from 'modules/search/components/SearchLoading';
import React, { PureComponent } from 'react';
import { compile, extractSearchBasePath, parse } from 'utils/searchURLs';

import { STATUS_INITIAL } from '../../../constants';
import fixUrlDecoding from '../helpers/fixUrlDecoding';

// Check if the store is in sync because it's filled async.
const filtersAreSynced = (storeState, expected) =>
  isEqual(storeState, expected);

function isSearchUrl(url) {
  return url.includes('/experiences') || url.includes('/s/');
}

function SyncedUrlWithFiltersStoreOuter(ComposedComponent) {
  return class SyncedUrlWithFiltersStore extends PureComponent<{
    flux: any;
    history: any;
  }> {
    state = {
      parsedFilters: null,
      activeFiltersFromStore: this.props.flux.stores.FiltersStore.getFilters(),
      approvedLocationsLoading:
        this.props.flux.stores.LocationsStore.getApprovedLocations().status ===
        STATUS_INITIAL,
      initialized: false,
      syncingURL: null,
    };

    componentDidMount() {
      this.props.flux.stores.FiltersStore.on('change', this.onStoreChange);
      this.props.flux.stores.LocationsStore.on('change', this.onStoreChange);
      this.parseFilters();
    }

    componentDidUpdate() {
      this.parseFilters();
    }

    componentWillUnmount() {
      this.props.flux.stores.FiltersStore.removeListener(
        'change',
        this.onStoreChange,
      );
      this.props.flux.stores.LocationsStore.removeListener(
        'change',
        this.onStoreChange,
      );
    }

    parseFilters = () => {
      const url = new URL(document.location.href);
      const href = `${decodeURI(url.pathname)}${url.search}${url.hash}`;
      const isSearch = isSearchUrl(href);
      if (!isSearch || this.state.approvedLocationsLoading) {
        return; // Current route is a modal
      }
      let pathParts = href.split('/experiences');
      if (href.includes('/s/')) {
        pathParts = href.split('/s/').map((path) => `/${path}`);
      }
      const pathWithQuery = fixUrlDecoding(pathParts[pathParts.length - 1]);
      if (pathWithQuery !== this.state.syncingURL) {
        this.setState(
          { parsedFilters: null, syncingURL: pathWithQuery },
          () => {
            const approvedLocationsData =
              this.props.flux.stores.LocationsStore.getApprovedLocations().data;

            const oldFilters = this.props.flux.stores.FiltersStore.getFilters();
            const basePath = extractSearchBasePath(window.location.pathname);
            const parsedFilters: ParseReturn = parse(
              pathWithQuery,
              filters,
              approvedLocationsData,
              basePath,
            );

            if (parsedFilters.redirectUrl) {
              this.props.history.replace(parsedFilters.redirectUrl);
              return;
            }

            const parsedFiltersWithEssentialParams = keepEssentialFilters(
              parsedFilters,
              oldFilters,
            );

            // This makes sure date.from, adults and children are always in the url
            const expectedURL = compile(
              parsedFiltersWithEssentialParams.country,
              parsedFiltersWithEssentialParams.city,
              parsedFiltersWithEssentialParams.page,
              parsedFiltersWithEssentialParams.activeFilters,
              parsedFilters.unrecognizedQueryParams,
            );
            if (expectedURL !== pathWithQuery) {
              if (document.location.href.includes('/s/')) {
                this.props.history.replace(`/s${expectedURL}`);
              } else {
                this.props.history.replace(`/experiences${expectedURL}`);
              }
              return;
            }

            this.setState(
              {
                parsedFilters: parsedFiltersWithEssentialParams,
              },
              () =>
                setTimeout(() =>
                  this.props.flux.actions.filters.setFilters(
                    parsedFiltersWithEssentialParams,
                  ),
                ),
            );
          },
        );
      }
    };

    onStoreChange = () =>
      this.setState({
        activeFiltersFromStore:
          this.props.flux.stores.FiltersStore.getFilters(),
        approvedLocationsLoading:
          this.props.flux.stores.LocationsStore.getApprovedLocations()
            .status === STATUS_INITIAL,
      });

    render() {
      if (
        this.state.parsedFilters &&
        filtersAreSynced(
          this.state.activeFiltersFromStore,
          this.state.parsedFilters,
        )
      ) {
        return <ComposedComponent {...this.props} />;
      }

      return <SearchLoading isLoading isFullScreen />;
    }
  };
}

export default SyncedUrlWithFiltersStoreOuter;
